import { useState, useCallback, useRef } from 'react';
import {
  useInitiateUploadMutation,
  useCompleteUploadMutation,
  useAbortUploadMutation,
  useRegisterUploadMutation,
  UploadPurpose,
  SingleUploadData,
  MultipartUploadData,
  RegisteredFile,
} from '../app/services/uploadApi';
import useUnloadGuard from './useUnloadGuard';
import { isFileTypeAccepted } from '../utils/fileType';

// ===== フォルダアップロード用の型 =====

/** フォルダ情報付きファイル */
export interface FileWithPath {
  file: File;
  /** フォルダ内の相対パス（例: "フォルダ名/サブフォルダ/ファイル.mp3"） */
  relativePath: string;
}

/** FileまたはFileWithPathの判定 */
export const isFileWithPath = (item: File | FileWithPath): item is FileWithPath => {
  return typeof item === 'object' && 'file' in item && 'relativePath' in item;
};

/** relativePathからフォルダパス（ファイル名を除いた部分）を取得 */
export const getFolderPathFromRelativePath = (relativePath: string): string | undefined => {
  const lastSlashIndex = relativePath.lastIndexOf('/');
  if (lastSlashIndex === -1) return undefined;
  return relativePath.substring(0, lastSlashIndex);
};

// ===== ファイル制限オプション（共通型） =====

/** ファイル制限設定 */
export interface FileRestrictions {
  /** 許可するファイルタイプ（MIMEタイプ or 拡張子） */
  accept?: string[];
  /** 最大ファイルサイズ（bytes） */
  maxSize?: number;
  /** 最大ファイル数 */
  maxFiles?: number;
  /** 最小ファイルサイズ（bytes） */
  minSize?: number;
}

/** バリデーションエラーの種別 */
export type ValidationErrorType =
  | 'invalid_type'
  | 'file_too_large'
  | 'file_too_small'
  | 'too_many_files';

/** バリデーションエラー */
export interface ValidationError {
  type: ValidationErrorType;
  file: File;
  message: string;
}

// ===== アップロード状態 =====

/** アップロード中ファイルのステータス */
export type UploadStatus = 'pending' | 'uploading' | 'completed' | 'error';

/**
 * S3 転送後の後処理（DB 登録など）のファイル単位の状態。
 * 後処理を行う画面だけが設定する（設定しなければ従来どおり ✓ を表示する）。
 */
export type FilePostStatus = 'waiting' | 'running' | 'done' | 'error';

/** アップロード中ファイルの情報 */
export interface UploadingFile {
  id: string;
  file: File;
  progress: number;
  status: UploadStatus;
  error?: string;
  result?: RegisteredFile;
  fileKey?: string;
  /** initiate が返した公開オブジェクト URL（返す purpose のみ。register 不要の埋め込み用） */
  fileUrl?: string;
  uploadId?: string;
  /** フォルダ内の相対パス（フォルダアップロード時のみ） */
  relativePath?: string;
  /** S3 転送後の後処理の状態 */
  postStatus?: FilePostStatus;
  /** 後処理が失敗した理由 */
  postError?: string;
}

// ===== 後処理フェーズ =====

/**
 * S3 アップロード完了後に走るクライアント主導の後処理の既定ラベル。
 *
 * S3 への転送が終わっても、DB 登録やメッセージ送信などの API 呼び出しが
 * 残っているケースが多い。この区間もブラウザが閉じられると中断するため、
 * アップロードと同じく「処理中」として扱う。
 *
 * 進捗はファイル単位（`UploadingFile.postStatus`）で表示するため、
 * フェーズ側が持つのは「いま何をしているか」を示すラベルだけでよい。
 */
const DEFAULT_POST_PROCESS_LABEL = '処理中';

// ===== フックオプション =====

/** useS3Uploadフックのオプション */
export interface UseS3UploadOptions {
  purpose: UploadPurpose;
  /** purpose=project_message 時に必須 */
  projectId?: string;
  /** purpose=shot_comment 時に必須 */
  shotId?: string;
  /** purpose=shared_library 時に必須（対象セクションの編集権限が必要） */
  sectionId?: string;
  /** ファイル制限設定 */
  restrictions?: FileRestrictions;
  /** true の場合、S3アップロード後の register ステップをスキップする（アバター等、別APIで管理するファイル向け） */
  skipRegister?: boolean;
  /** バリデーションエラー時のコールバック */
  onValidationError?: (errors: ValidationError[]) => void;
  /** 個別ファイル完了時のコールバック */
  onFileComplete?: (uploadingFile: UploadingFile) => void;
  /** 全ファイル完了時のコールバック */
  onAllComplete?: (files: UploadingFile[]) => void;
  /** エラー時のコールバック */
  onError?: (file: UploadingFile, error: Error) => void;
}

// ===== フック戻り値 =====

/** useS3Uploadフックの戻り値 */
export interface UseS3UploadReturn {
  /** ファイルをアップロード（File[]またはFileWithPath[]を受け取り可能） */
  uploadFiles: (files: File[] | FileWithPath[]) => Promise<UploadingFile[]>;
  /** アップロード中のファイル一覧 */
  uploadingFiles: UploadingFile[];
  /** アップロード中かどうか */
  isUploading: boolean;
  /**
   * S3 アップロード中、または後処理中かどうか。
   * 「閉じたら中断される区間」の判定にはこちらを使う。
   */
  isBusy: boolean;
  /** 後処理中に表示するラベル（後処理中でなければ null） */
  postProcessLabel: string | null;
  /** 後処理フェーズを開始する（入れ子・並行は想定しない） */
  beginPostProcess: (label?: string) => void;
  /** 後処理フェーズを終了する */
  endPostProcess: () => void;
  /**
   * ファイル単位の後処理状態を更新する。
   * 設定すると進捗行が ✓ ではなく後処理の状態を表示するようになる。
   */
  setPostStatus: (fileId: string, status: FilePostStatus, error?: string) => void;
  /**
   * 件数不定の後処理を実行する。`fn` の実行中だけ後処理フェーズになる。
   * 単発の API 呼び出し（メッセージ送信など）を包む用途。
   * 並行して呼んでも安全（最後の 1 つが終わるまでフェーズを維持する）。
   */
  withPostProcess: <T>(fn: () => Promise<T>, label?: string) => Promise<T>;
  /** 全体の進捗（0-100） */
  totalProgress: number;
  /** 特定ファイルのアップロードを中止 */
  abortFile: (fileId: string) => void;
  /** 全てのアップロードを中止 */
  abortAll: () => void;
  /** 完了/エラーファイルをクリア */
  clearCompleted: () => void;
  /**
   * 失敗した行だけを一覧から取り除く。
   * 「失敗分を除いて続行」したときに、成功分の行を残したまま失敗分だけ畳む。
   */
  clearFailed: () => void;
  /** エラーになったファイルを再アップロード（File は state に保持済み） */
  retryFile: (fileId: string) => Promise<UploadingFile | null>;
  /** エラーになった複数ファイルをまとめて再アップロード */
  retryFiles: (fileIds: string[]) => Promise<UploadingFile[]>;
  /** 現在のファイル制限設定 */
  restrictions: FileRestrictions | undefined;
}

// ===== ヘルパー関数 =====

/** 除外すべきシステムファイル名のパターン */
const EXCLUDED_FILE_PATTERNS = [
  '.DS_Store',
  'Thumbs.db',
  '.gitkeep',
  '.gitignore',
  'desktop.ini',
];

/** ファイルがシステムファイルかどうかを判定 */
const isSystemFile = (fileName: string): boolean => {
  const baseName = fileName.split('/').pop() || fileName;
  return EXCLUDED_FILE_PATTERNS.includes(baseName) || baseName.startsWith('._');
};

/** システムファイルを除外 */
const filterSystemFiles = (
  files: (File | FileWithPath)[],
): (File | FileWithPath)[] => {
  return files.filter((item) => {
    const fileName = isFileWithPath(item) ? item.file.name : item.name;
    return !isSystemFile(fileName);
  });
};

/** UUIDを生成 */
const generateId = (): string => {
  return crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/** ファイルサイズをフォーマット */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/** ファイルをバリデート */
const validateFiles = (
  files: File[],
  restrictions?: FileRestrictions,
  existingCount: number = 0
): { valid: File[]; errors: ValidationError[] } => {
  const valid: File[] = [];
  const errors: ValidationError[] = [];

  // maxFilesチェック（全体の数）
  if (restrictions?.maxFiles) {
    // 既存件数が上限を超えている場合は負値になり、slice が末尾切り出しに化けて
    // 超過分の判定が反転するため 0 で下限を切る
    const totalAllowed = Math.max(0, restrictions.maxFiles - existingCount);
    if (files.length > totalAllowed) {
      // 超過分をエラーとして報告
      files.slice(totalAllowed).forEach((file) => {
        errors.push({
          type: 'too_many_files',
          file,
          message: `ファイル数が多すぎます（最大: ${restrictions.maxFiles}件）`,
        });
      });
      files = files.slice(0, totalAllowed);
    }
  }

  for (const file of files) {
    // acceptチェック
    if (!isFileTypeAccepted(file, restrictions?.accept)) {
      errors.push({
        type: 'invalid_type',
        file,
        message: 'このファイル形式はサポートされていません',
      });
      continue;
    }

    // maxSizeチェック
    if (restrictions?.maxSize && file.size > restrictions.maxSize) {
      errors.push({
        type: 'file_too_large',
        file,
        message: `ファイルサイズが大きすぎます（最大: ${formatFileSize(restrictions.maxSize)}）`,
      });
      continue;
    }

    // minSizeチェック
    if (restrictions?.minSize && file.size < restrictions.minSize) {
      errors.push({
        type: 'file_too_small',
        file,
        message: `ファイルサイズが小さすぎます（最小: ${formatFileSize(restrictions.minSize)}）`,
      });
      continue;
    }

    valid.push(file);
  }

  return { valid, errors };
};

// ===== 並列アップロード設定 =====
const MAX_CONCURRENT_PARTS = 5;

// ===== リトライ設定 =====
const MAX_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 1000;

/** リトライ可能なエラーかどうかを判定 */
const isRetryableError = (status: number): boolean => {
  // 5xx系エラーまたはネットワークエラー（status=0）はリトライ可能
  // 403（presigned URL期限切れ）はリトライしても無駄なので除外
  return status >= 500 || status === 0;
};

/** 指数バックオフでの待機 */
const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/** セマフォ: 並列度を制御するためのクラス */
class Semaphore {
  private permits: number;
  private waiting: Array<() => void> = [];

  constructor(permits: number) {
    this.permits = permits;
  }

  async acquire(): Promise<void> {
    if (this.permits > 0) {
      this.permits--;
      return;
    }
    return new Promise<void>((resolve) => {
      this.waiting.push(resolve);
    });
  }

  release(): void {
    if (this.waiting.length > 0) {
      const next = this.waiting.shift();
      next?.();
    } else {
      this.permits++;
    }
  }
}

// ===== メインフック =====

export const useS3Upload = (options: UseS3UploadOptions): UseS3UploadReturn => {
  const {
    purpose,
    projectId,
    shotId,
    sectionId,
    restrictions,
    skipRegister = false,
    onValidationError,
    onFileComplete,
    onAllComplete,
    onError,
  } = options;

  // RTK Query mutations
  const [initiateUpload] = useInitiateUploadMutation();
  const [completeUpload] = useCompleteUploadMutation();
  const [abortUpload] = useAbortUploadMutation();
  const [registerUpload] = useRegisterUploadMutation();

  // State
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [postProcessLabel, setPostProcessLabel] = useState<string | null>(null);

  // retryFiles が最新のファイル一覧を同期的に参照するための ref
  const uploadingFilesRef = useRef<UploadingFile[]>([]);
  uploadingFilesRef.current = uploadingFiles;

  // setTimeout 経由の遅延呼び出しから最新の後処理状態を参照するための ref
  const postProcessRef = useRef<string | null>(null);
  postProcessRef.current = postProcessLabel;

  // Refs for abort controllers
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  
  // 同じ folder_path の registerUpload を直列化するための Promise チェーン
  const folderRegisterChainRef = useRef<Map<string, Promise<unknown>>>(new Map());

  // アップロード中かどうか
  const isUploading = uploadingFiles.some(
    (f) => f.status === 'pending' || f.status === 'uploading'
  );

  const isPostProcessing = postProcessLabel !== null;

  // S3 転送中か後処理中か。どちらもタブを閉じると中断される区間
  const isBusy = isUploading || isPostProcessing;

  // 中断されると取りこぼしが出るため、処理中の離脱を引き止める。
  // フック内で張ることで、全てのアップロード箇所が自動的に保護される
  useUnloadGuard(isBusy);

  // 全体の進捗を計算（サイズ加重平均）
  // 大小混在時も体感と一致する進捗表示
  const totalProgress = (() => {
    if (uploadingFiles.length === 0) return 0;
    
    const totalSize = uploadingFiles.reduce((sum, f) => sum + f.file.size, 0);
    if (totalSize === 0) return 0;
    
    const weightedProgress = uploadingFiles.reduce(
      (sum, f) => sum + (f.progress * f.file.size),
      0
    );
    
    return weightedProgress / totalSize;
  })();

  // ファイルステータスを更新
  const updateFileStatus = useCallback(
    (fileId: string, updates: Partial<UploadingFile>) => {
      setUploadingFiles((prev) =>
        prev.map((f) => (f.id === fileId ? { ...f, ...updates } : f))
      );
    },
    []
  );

  // シングルアップロード（presigned POST）
  const uploadSingle = useCallback(
    async (
      uploadingFile: UploadingFile,
      data: SingleUploadData,
      abortSignal: AbortSignal
    ): Promise<void> => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const formData = new FormData();

        // presigned POSTのフィールドを追加
        Object.entries(data.presigned_post.fields).forEach(([key, value]) => {
          formData.append(key, value);
        });
        // ファイルは最後に追加
        formData.append('file', uploadingFile.file);

        // 進捗イベント
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            updateFileStatus(uploadingFile.id, { progress });
          }
        };

        // 完了イベント
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };

        // エラーイベント
        xhr.onerror = () => {
          reject(new Error('Network error during upload'));
        };

        // 中止イベント
        xhr.onabort = () => {
          reject(new Error('Upload aborted'));
        };

        // AbortSignalのリスナー
        abortSignal.addEventListener('abort', () => {
          xhr.abort();
        });

        // アップロード開始
        xhr.open('POST', data.presigned_post.url);
        xhr.send(formData);
      });
    },
    [updateFileStatus]
  );

  // 単一パートのアップロード（リトライ付き）
  const uploadPartWithRetry = useCallback(
    async (
      partInfo: { part_number: number; url: string },
      chunk: Blob,
      abortSignal: AbortSignal
    ): Promise<{ part_number: number; etag: string }> => {
      let lastError: Error | null = null;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        if (abortSignal.aborted) {
          throw new Error('Upload aborted');
        }

        try {
          const response = await fetch(partInfo.url, {
            method: 'PUT',
            body: chunk,
            signal: abortSignal,
          });

          if (!response.ok) {
            // 403（presigned URL期限切れ）はリトライしない
            if (response.status === 403) {
              throw new Error(`Part ${partInfo.part_number} upload failed: presigned URL expired (403)`);
            }
            
            // リトライ可能なエラーかチェック
            if (isRetryableError(response.status) && attempt < MAX_RETRIES) {
              const delay = RETRY_BASE_DELAY_MS * Math.pow(2, attempt);
              console.warn(`Part ${partInfo.part_number} failed with status ${response.status}, retrying in ${delay}ms (attempt ${attempt + 1}/${MAX_RETRIES})`);
              await sleep(delay);
              continue;
            }
            
            throw new Error(`Part ${partInfo.part_number} upload failed with status ${response.status}`);
          }

          const etag = response.headers.get('ETag');
          if (!etag) {
            throw new Error(`No ETag returned for part ${partInfo.part_number}`);
          }

          return {
            part_number: partInfo.part_number,
            etag: etag,
          };
        } catch (error) {
          // ネットワークエラーの場合
          if (error instanceof Error) {
            if (error.name === 'AbortError') {
              throw new Error('Upload aborted');
            }
            
            lastError = error;
            
            // リトライ可能なネットワークエラー
            if (attempt < MAX_RETRIES && !error.message.includes('403')) {
              const delay = RETRY_BASE_DELAY_MS * Math.pow(2, attempt);
              console.warn(`Part ${partInfo.part_number} network error, retrying in ${delay}ms (attempt ${attempt + 1}/${MAX_RETRIES}):`, error.message);
              await sleep(delay);
              continue;
            }
          }
          
          throw error;
        }
      }

      throw lastError || new Error(`Part ${partInfo.part_number} upload failed after ${MAX_RETRIES} retries`);
    },
    []
  );

  // マルチパートアップロード（並列 + リトライ対応）
  const uploadMultipart = useCallback(
    async (
      uploadingFile: UploadingFile,
      data: MultipartUploadData,
      abortSignal: AbortSignal
    ): Promise<Array<{ part_number: number; etag: string }>> => {
      const totalParts = data.part_urls.length;
      let completedParts = 0;
      
      // 並列度制御用セマフォ
      const semaphore = new Semaphore(MAX_CONCURRENT_PARTS);
      
      // 各パートを並列でアップロード
      const uploadPromises = data.part_urls.map(async (partInfo) => {
        // セマフォを取得（並列度を制限）
        await semaphore.acquire();
        
        try {
          if (abortSignal.aborted) {
            throw new Error('Upload aborted');
          }

          const start = (partInfo.part_number - 1) * data.part_size;
          const end = Math.min(start + data.part_size, uploadingFile.file.size);
          const chunk = uploadingFile.file.slice(start, end);

          // リトライ付きでパートをアップロード
          const result = await uploadPartWithRetry(partInfo, chunk, abortSignal);

          // 完了したパートをカウント（アトミックではないが、表示用なので許容）
          completedParts++;
          const progress = Math.round((completedParts / totalParts) * 100);
          updateFileStatus(uploadingFile.id, { progress });

          return result;
        } finally {
          // 必ずセマフォを解放
          semaphore.release();
        }
      });

      // 全パートの完了を待つ
      const parts = await Promise.all(uploadPromises);

      return parts;
    },
    [updateFileStatus, uploadPartWithRetry]
  );

  // 単一ファイルのアップロード処理
  const processFileUpload = useCallback(
    async (uploadingFile: UploadingFile) => {
      const abortController = new AbortController();
      abortControllersRef.current.set(uploadingFile.id, abortController);

      // abort処理の安定化: stateではなくローカル変数で保持
      // Reactの非同期state更新に依存しない設計
      let localFileKey: string | undefined;
      let localUploadId: string | undefined;

      try {
        updateFileStatus(uploadingFile.id, { status: 'uploading', progress: 0 });

        // 1. initiate - presigned URL取得（purposeに応じて project_id / shot_id を付与）
        const initiateResult = await initiateUpload({
          file_name: uploadingFile.file.name,
          file_size: uploadingFile.file.size,
          content_type: uploadingFile.file.type || 'application/octet-stream',
          purpose,
          project_id: projectId,
          shot_id: shotId,
          section_id: sectionId,
        }).unwrap();

        const uploadData = initiateResult.data;
        localFileKey = uploadData.file_key;

        updateFileStatus(uploadingFile.id, { fileKey: localFileKey });

        // 2. S3にアップロード
        if (uploadData.upload_type === 'single') {
          await uploadSingle(
            uploadingFile,
            uploadData as SingleUploadData,
            abortController.signal
          );
        } else {
          const multipartData = uploadData as MultipartUploadData;
          localUploadId = multipartData.upload_id;
          updateFileStatus(uploadingFile.id, { uploadId: localUploadId });

          const parts = await uploadMultipart(
            uploadingFile,
            multipartData,
            abortController.signal
          );

          // 3. complete（マルチパートの場合のみ）
          await completeUpload({
            file_key: localFileKey,
            upload_id: localUploadId,
            parts,
          }).unwrap();
        }

        // 4. register - DBにメタデータ登録（skipRegister の場合はスキップ）
        let registerResultData: RegisteredFile | undefined;

        if (!skipRegister) {
          const folderPath = uploadingFile.relativePath 
            ? getFolderPathFromRelativePath(uploadingFile.relativePath)
            : undefined;

          const doRegister = async () => {
            const result = await registerUpload({
              file_key: localFileKey!,
              purpose,
              project_id: projectId,
              ...(folderPath && { folder_path: folderPath }),
            }).unwrap();
            return result.data;
          };

          if (folderPath) {
            // 同一 folder_path の register を直列化して重複フォルダ作成を防ぐ
            const prev = folderRegisterChainRef.current.get(folderPath) ?? Promise.resolve();
            const current = prev.then(doRegister, doRegister);
            folderRegisterChainRef.current.set(folderPath, current);
            registerResultData = await current;
          } else {
            registerResultData = await doRegister();
          }
        }

        // 完了
        const completedFile: UploadingFile = {
          ...uploadingFile,
          status: 'completed',
          progress: 100,
          result: registerResultData,
          fileKey: localFileKey,
          fileUrl: uploadData.file_url,
        };

        updateFileStatus(uploadingFile.id, {
          status: 'completed',
          progress: 100,
          result: registerResultData,
          fileUrl: uploadData.file_url,
        });

        onFileComplete?.(completedFile);

        return completedFile;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed';
        // 中止の場合はマルチパートをクリーンアップ
        // ローカル変数を使用してstateの非同期更新に依存しない
        if (localUploadId && localFileKey) {
          try {
            await abortUpload({
              file_key: localFileKey,
              upload_id: localUploadId,
            }).unwrap();
          } catch {
            // クリーンアップエラーは無視
          }
        }

        updateFileStatus(uploadingFile.id, {
          status: 'error',
          error: errorMessage,
        });

        const errorFile: UploadingFile = {
          ...uploadingFile,
          status: 'error',
          error: errorMessage,
        };

        onError?.(errorFile, error instanceof Error ? error : new Error(errorMessage));

        return errorFile;
      } finally {
        abortControllersRef.current.delete(uploadingFile.id);
      }
    },
    [
      initiateUpload,
      completeUpload,
      abortUpload,
      registerUpload,
      uploadSingle,
      uploadMultipart,
      updateFileStatus,
      purpose,
      projectId,
      shotId,
      sectionId,
      skipRegister,
      onFileComplete,
      onError,
    ]
  );

  // ファイルをアップロード（File[]またはFileWithPath[]を受け取り可能）
  const uploadFiles = useCallback(
    async (filesInput: File[] | FileWithPath[]): Promise<UploadingFile[]> => {
      // システムファイル（.DS_Store等）を除外
      const filteredInput = filterSystemFiles(filesInput);
      
      if (filteredInput.length === 0) return [];
      
      // FileWithPath[]の場合はFileとrelativePathを分離
      const isFileWithPathArray = filteredInput.length > 0 && isFileWithPath(filteredInput[0]);
      
      let files: File[];
      let relativePathMap: Map<string, string> | undefined;
      
      if (isFileWithPathArray) {
        const filesWithPath = filteredInput as FileWithPath[];
        files = filesWithPath.map(f => f.file);
        relativePathMap = new Map(
          filesWithPath.map(f => [f.file.name + f.file.size + f.file.lastModified, f.relativePath])
        );
      } else {
        files = filteredInput as File[];
      }
      
      // バリデーション
      const { valid, errors } = validateFiles(
        files,
        restrictions,
        uploadingFiles.filter((f) => f.status !== 'error' && f.status !== 'completed').length
      );

      // バリデーションエラーがあれば通知
      if (errors.length > 0) {
        onValidationError?.(errors);
      }

      if (valid.length === 0) return [];

      // アップロード中ファイルを作成（relativePath情報を含む）
      const newUploadingFiles: UploadingFile[] = valid.map((file) => {
        const fileKey = file.name + file.size + file.lastModified;
        return {
          id: generateId(),
          file,
          progress: 0,
          status: 'pending' as UploadStatus,
          relativePath: relativePathMap?.get(fileKey),
        };
      });

      setUploadingFiles((prev) => [...prev, ...newUploadingFiles]);

      // 並列でアップロード
      const results = await Promise.all(
        newUploadingFiles.map((f) => processFileUpload(f))
      );

      // 全て完了したらコールバック
      const allCompleted = results.every((r) => r.status === 'completed');
      if (allCompleted) {
        onAllComplete?.(results);
      }

      return results;
    },
    [restrictions, uploadingFiles, onValidationError, processFileUpload, onAllComplete]
  );

  // エラーになったファイルを再アップロード（error 行を pending に戻して再実行）。
  // File オブジェクトは state に保持しているため、そのまま同じ行で再試行できる
  const retryFiles = useCallback(
    async (fileIds: string[]): Promise<UploadingFile[]> => {
      const idSet = new Set(fileIds);
      const targets = uploadingFilesRef.current.filter(
        (f) => idSet.has(f.id) && f.status === 'error'
      );
      if (targets.length === 0) return [];

      const targetIds = new Set(targets.map((f) => f.id));
      setUploadingFiles((prev) =>
        prev.map((f) =>
          targetIds.has(f.id)
            ? {
                ...f,
                status: 'pending' as UploadStatus,
                progress: 0,
                error: undefined,
                postStatus: undefined,
                postError: undefined,
              }
            : f
        )
      );

      return Promise.all(
        targets.map((f) =>
          processFileUpload({
            ...f,
            status: 'pending',
            progress: 0,
            error: undefined,
            postStatus: undefined,
            postError: undefined,
          })
        )
      );
    },
    [processFileUpload]
  );

  const retryFile = useCallback(
    async (fileId: string): Promise<UploadingFile | null> => {
      const [result] = await retryFiles([fileId]);
      return result ?? null;
    },
    [retryFiles]
  );

  // 特定ファイルのアップロードを中止
  const abortFile = useCallback((fileId: string) => {
    const controller = abortControllersRef.current.get(fileId);
    if (controller) {
      controller.abort();
    }
  }, []);

  // 全てのアップロードを中止
  const abortAll = useCallback(() => {
    abortControllersRef.current.forEach((controller) => {
      controller.abort();
    });
  }, []);

  // ===== 後処理フェーズの制御 =====

  const beginPostProcess = useCallback(
    (label: string = DEFAULT_POST_PROCESS_LABEL) => {
      postProcessRef.current = label;
      setPostProcessLabel(label);
    },
    []
  );

  const endPostProcess = useCallback(() => {
    postProcessRef.current = null;
    setPostProcessLabel(null);
  }, []);

  const setPostStatus = useCallback(
    (fileId: string, status: FilePostStatus, error?: string) => {
      updateFileStatus(fileId, { postStatus: status, postError: error });
    },
    [updateFileStatus]
  );

  // withPostProcess は onFileComplete 経由などで並行して呼ばれうるため、
  // 参照カウントで管理し、最後の 1 つが終わるまで後処理フェーズを維持する
  const postProcessDepthRef = useRef(0);

  const withPostProcess = useCallback(
    async <T,>(
      fn: () => Promise<T>,
      label: string = DEFAULT_POST_PROCESS_LABEL,
    ): Promise<T> => {
      postProcessDepthRef.current += 1;
      if (postProcessDepthRef.current === 1) {
        beginPostProcess(label);
      }
      try {
        return await fn();
      } finally {
        postProcessDepthRef.current -= 1;
        if (postProcessDepthRef.current === 0) {
          endPostProcess();
        }
      }
    },
    [beginPostProcess, endPostProcess]
  );

  // 完了/エラーファイルをクリア。
  // 後処理中は進捗を畳まない（S3 転送が終わっただけで「完了」に見えてしまい、
  // ユーザーがタブを閉じる原因になるため）。レンダー時点の判定と、
  // setTimeout 経由の遅延呼び出し向けの ref 判定を併用する。
  // 後処理の終了で識別子が変わるので、呼び出し側の自動クローズ effect が
  // 再実行され、そこで初めて進捗が畳まれる。
  const clearCompleted = useCallback(() => {
    if (isPostProcessing || postProcessRef.current) return;
    setUploadingFiles((prev) =>
      prev.filter((f) => f.status !== 'completed' && f.status !== 'error')
    );
  }, [isPostProcessing]);

  const clearFailed = useCallback(() => {
    setUploadingFiles((prev) => prev.filter((f) => f.status !== 'error'));
  }, []);

  return {
    uploadFiles,
    uploadingFiles,
    isUploading,
    isBusy,
    postProcessLabel,
    beginPostProcess,
    endPostProcess,
    setPostStatus,
    withPostProcess,
    totalProgress,
    abortFile,
    abortAll,
    clearCompleted,
    clearFailed,
    retryFile,
    retryFiles,
    restrictions,
  };
};

export default useS3Upload;
